import GuestImport from "./GuestImport";
// engagement-components/GuestCRM.jsx
// Cleaned production-ready page: duplicate GuestFormPanel block removed,
// filters/table/modals preserved, and component boundaries fixed.

import React, { useState, useEffect, useRef } from "react";
import {
  Row,
  Col,
  Card,
  Table,
  Badge,
  Button,
  Form,
  InputGroup,
  Alert,
  Spinner,
  OverlayTrigger,
  Tooltip,
} from "react-bootstrap";
import {
  FaSearch,
  FaPlus,
  FaEdit,
  FaTrash,
  FaDownload,
  FaUpload,
  FaQrcode,
  FaUser,
  FaSync,
  FaHistory,
  FaWhatsapp,
  FaEnvelope,
  FaBed,
  FaStar,
  FaFileImport,
  FaCheckCircle,
  FaCopy,
} from "react-icons/fa";
import { QRCodeSVG as QRCode } from "qrcode.react";
import { Dropdown, Menu } from "antd";
import { DownOutlined } from "@ant-design/icons";
import axios from "axios";
import dayjs from "dayjs";
import { apiUrl, authHeaders, cdnUrl } from "./config";
import { RSelect, RightModal, useNotify } from "./shared";

const SOURCES = ["PMS", "CSV Import", "Manual", "Feedback Form", "QR Registration"];
const TAGS = [
  "VIP",
  "Regular",
  "First Visit",
  "Loyalty Member",
  "Corporate",
  "Honeymoon",
  "Anniversary Stay",
  "Birthday Stay",
];
const STATUS_OPTIONS = ["Active", "Inactive", "Blocked"];
const SOURCE_COLOR = {
  PMS: "primary",
  "CSV Import": "info",
  Manual: "secondary",
  "Feedback Form": "success",
  "QR Registration": "warning",
};
const SOURCE_ICON = {
  PMS: "fa-hotel",
  "CSV Import": "fa-file-csv",
  Manual: "fa-user-plus",
  "Feedback Form": "fa-poll",
  "QR Registration": "fa-qrcode",
};

const CSV_HEADERS = [
  "name",
  "phone",
  "email",
  "dob",
  "anniversary",
  "room_number",
  "checkin_date",
  "checkout_date",
  "tags",
  "notes",
];

const emptyCustomer = () => ({
  id: null,
  name: "",
  phone: "",
  email: "",
  dob: "",
  anniversary: "",
  room_number: "",
  checkin_date: "",
  checkout_date: "",
  tags: [],
  notes: "",
  source: "Manual",
  status: "Active",
});

const C = {
  brand: "#4f46e5",
  brandLt: "#eef2ff",
  green: "#059669",
  greenLt: "#ecfdf5",
  text: "#111827",
  muted: "#6b7280",
  subtle: "#9ca3af",
  border: "#e5e7eb",
  bg: "#f9fafb",
  white: "#ffffff",
  danger: "#ef4444",
  amber: "#d97706",
};

const TagPill = ({ tag }) => (
  <span
    className="badge me-1 mb-1"
    style={{ background: "#e0e7ff", color: "#3730a3", fontSize: "0.68rem" }}
  >
    {tag}
  </span>
);

const Lbl = ({ children, required }) => (
  <div
    style={{
      fontSize: "0.68rem",
      fontWeight: 800,
      textTransform: "uppercase",
      letterSpacing: "0.07em",
      color: C.subtle,
      marginBottom: 5,
    }}
  >
    {children}
    {required && <span style={{ color: C.danger, marginLeft: 3 }}>*</span>}
  </div>
);

const Field = ({ children, style }) => (
  <div style={{ display: "flex", flexDirection: "column", ...style }}>{children}</div>
);

const Inp = ({ icon, style, ...rest }) => (
  <div style={{ position: "relative" }}>
    {icon && (
      <span
        style={{
          position: "absolute",
          left: 11,
          top: "50%",
          transform: "translateY(-50%)",
          color: C.subtle,
          fontSize: "0.82rem",
          pointerEvents: "none",
          zIndex: 1,
        }}
      >
        {icon}
      </span>
    )}
    <input
      {...rest}
      style={{
        width: "100%",
        padding: icon ? "8px 11px 8px 34px" : "8px 11px",
        fontSize: "0.84rem",
        border: `1px solid ${C.border}`,
        borderRadius: 9,
        outline: "none",
        color: C.text,
        background: C.white,
        boxSizing: "border-box",
        ...style,
      }}
      onFocus={(e) => {
        e.target.style.borderColor = C.brand;
        e.target.style.boxShadow = `0 0 0 3px ${C.brandLt}`;
      }}
      onBlur={(e) => {
        e.target.style.borderColor = C.border;
        e.target.style.boxShadow = "none";
      }}
    />
  </div>
);

const DateInp = ({ value, onChange, placeholder }) => (
  <div style={{ position: "relative" }}>
    <i
      className="fas fa-calendar-alt"
      style={{
        position: "absolute",
        left: 11,
        top: "50%",
        transform: "translateY(-50%)",
        color: C.subtle,
        fontSize: "0.78rem",
        pointerEvents: "none",
      }}
    />
    <input
      type="date"
      value={value || ""}
      onChange={(e) => onChange(e.target.value)}
      placeholder={placeholder}
      style={{
        width: "100%",
        padding: "8px 11px 8px 34px",
        fontSize: "0.84rem",
        border: `1px solid ${C.border}`,
        borderRadius: 9,
        outline: "none",
        color: value ? C.text : C.subtle,
        background: C.white,
        boxSizing: "border-box",
      }}
      onFocus={(e) => {
        e.target.style.borderColor = C.brand;
        e.target.style.boxShadow = `0 0 0 3px ${C.brandLt}`;
      }}
      onBlur={(e) => {
        e.target.style.borderColor = C.border;
        e.target.style.boxShadow = "none";
      }}
    />
  </div>
);

const Sel = ({ value, onChange, options, placeholder }) => (
  <select
    value={value || ""}
    onChange={(e) => onChange(e.target.value)}
    style={{
      width: "100%",
      padding: "8px 11px",
      fontSize: "0.84rem",
      border: `1px solid ${C.border}`,
      borderRadius: 9,
      outline: "none",
      color: value ? C.text : C.subtle,
      background: C.white,
      boxSizing: "border-box",
    }}
    onFocus={(e) => {
      e.target.style.borderColor = C.brand;
      e.target.style.boxShadow = `0 0 0 3px ${C.brandLt}`;
    }}
    onBlur={(e) => {
      e.target.style.borderColor = C.border;
      e.target.style.boxShadow = "none";
    }}
  >
    {placeholder && <option value="">{placeholder}</option>}
    {options.map((o) => (
      <option key={o} value={o}>
        {o}
      </option>
    ))}
  </select>
);

const GuestFormPanel = ({ show, editingCustomer, onSave, onClose, saving, error }) => {
  const isEdit = !!editingCustomer?.id || !!editingCustomer?.guestid;
  const [form, setForm] = useState({ ...emptyCustomer(), ...editingCustomer });
  const [tagInput, setTagInput] = useState("");
  const set = (field, val) => setForm((p) => ({ ...p, [field]: val }));

  useEffect(() => {
    setForm({ ...emptyCustomer(), ...(editingCustomer || {}) });
    setTagInput("");
  }, [editingCustomer, show]);

  const toggleTag = (tag) =>
    setForm((p) => ({
      ...p,
      tags: (p.tags || []).includes(tag)
        ? (p.tags || []).filter((t) => t !== tag)
        : [...(p.tags || []), tag],
    }));

  const addCustomTag = () => {
    const t = tagInput.trim();
    if (t && !(form.tags || []).includes(t)) toggleTag(t);
    setTagInput("");
  };

  if (!show) return null;

  const COMPLETE_FIELDS = [
    { key: "name", label: "Name" },
    { key: "phone", label: "Phone" },
    { key: "email", label: "Email" },
    { key: "dob", label: "Birthday" },
    { key: "anniversary", label: "Anniversary" },
    { key: "checkin_date", label: "Check-in" },
  ];

  const filled = COMPLETE_FIELDS.filter((f) => form[f.key]);
  const missing = COMPLETE_FIELDS.filter((f) => !form[f.key]);
  const pct = Math.round((filled.length / COMPLETE_FIELDS.length) * 100);
  const pctColor = pct >= 80 ? C.green : pct >= 50 ? C.amber : C.danger;
  const pctGrad =
    pct >= 80
      ? `linear-gradient(90deg, ${C.green}, #34d399)`
      : pct >= 50
      ? `linear-gradient(90deg, ${C.amber}, #fbbf24)`
      : `linear-gradient(90deg, ${C.danger}, #f87171)`;

  return (
    <div style={{ position: "fixed", inset: 0, zIndex: 1060, display: "flex", alignItems: "stretch" }}>
      <div onClick={onClose} style={{ flex: 1, background: "rgba(0,0,0,0.38)", cursor: "pointer" }} />
      <div
        style={{
          width: 540,
          flexShrink: 0,
          background: C.white,
          display: "flex",
          flexDirection: "column",
          height: "100vh",
          boxShadow: "-8px 0 40px rgba(0,0,0,0.14)",
        }}
      >
        <div style={{ padding: "18px 24px 14px", borderBottom: `1px solid ${C.border}`, flexShrink: 0 }}>
          <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 14 }}>
            <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
              <div
                style={{
                  width: 42,
                  height: 42,
                  borderRadius: 12,
                  background: isEdit ? "#eff6ff" : C.brandLt,
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  flexShrink: 0,
                }}
              >
                <i className={`fas ${isEdit ? "fa-user-edit" : "fa-user-plus"}`} style={{ color: C.brand, fontSize: "1rem" }} />
              </div>
              <div>
                <div style={{ fontSize: "1rem", fontWeight: 800, color: C.text, lineHeight: 1.2 }}>
                  {isEdit ? "Edit Guest" : "Add New Guest"}
                </div>
                <div style={{ fontSize: "0.72rem", color: C.subtle, marginTop: 2 }}>
                  {isEdit ? `Editing: ${editingCustomer?.name || "Guest"}` : "Fill in the details — more fields = smarter journeys"}
                </div>
              </div>
            </div>
            <button
              onClick={onClose}
              style={{
                width: 34,
                height: 34,
                borderRadius: "50%",
                border: `1px solid ${C.border}`,
                background: C.bg,
                cursor: "pointer",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                fontSize: "1.1rem",
                color: C.muted,
                flexShrink: 0,
              }}
            >
              ×
            </button>
          </div>

          <div>
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 5 }}>
              <span
                style={{
                  fontSize: "0.68rem",
                  fontWeight: 800,
                  textTransform: "uppercase",
                  letterSpacing: "0.07em",
                  color: C.subtle,
                }}
              >
                Profile completeness
              </span>
              <span style={{ fontSize: "0.72rem", fontWeight: 800, color: pctColor }}>{pct}%</span>
            </div>
            <div style={{ height: 5, borderRadius: 99, background: "#f1f5f9", overflow: "hidden", marginBottom: 5 }}>
              <div style={{ height: "100%", borderRadius: 99, width: `${pct}%`, background: pctGrad, transition: "width 0.4s ease" }} />
            </div>
            {missing.length > 0 && <div style={{ fontSize: "0.68rem", color: C.subtle }}>Missing: {missing.map((f) => f.label).join(", ")}</div>}
          </div>
        </div>

        <div style={{ flex: 1, overflowY: "auto", padding: "18px 24px" }}>
          {error && (
            <div
              style={{
                padding: "10px 14px",
                borderRadius: 9,
                background: "#fef2f2",
                border: "1px solid #fecaca",
                color: C.danger,
                fontSize: "0.82rem",
                marginBottom: 16,
                display: "flex",
                alignItems: "center",
                gap: 8,
              }}
            >
              <i className="fas fa-exclamation-circle" /> {error}
            </div>
          )}

          <div style={{ background: "#fafbff", borderRadius: 14, padding: "16px 18px", border: "1px solid #e0e7ff", marginBottom: 14 }}>
            <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 14 }}>
              <div style={{ width: 28, height: 28, borderRadius: 8, background: C.brandLt, display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
                <i className="fas fa-id-card" style={{ color: C.brand, fontSize: "0.72rem" }} />
              </div>
              <div style={{ fontSize: "0.78rem", fontWeight: 800, color: C.text }}>Identity</div>
              <div style={{ fontSize: "0.71rem", color: C.subtle }}>— name, contact & dates</div>
            </div>

            <Field>
              <Lbl required>Full Name</Lbl>
              <Inp icon={<i className="fas fa-user" />} value={form.name} onChange={(e) => set("name", e.target.value)} placeholder="Rahul Sharma" autoFocus />
            </Field>

            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12, marginTop: 12 }}>
              <Field>
                <Lbl required>Phone / WhatsApp</Lbl>
                <Inp icon={<FaWhatsapp style={{ color: "#25d366" }} />} type="tel" value={form.phone} onChange={(e) => set("phone", e.target.value)} placeholder="+91 98765 43210" />
              </Field>
              <Field>
                <Lbl>Email</Lbl>
                <Inp icon={<FaEnvelope style={{ color: C.brand }} />} type="email" value={form.email} onChange={(e) => set("email", e.target.value)} placeholder="rahul@email.com" />
              </Field>
            </div>

            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12, marginTop: 12 }}>
              <Field>
                <Lbl>🎂 Birthday</Lbl>
                <DateInp value={form.dob} onChange={(v) => set("dob", v)} placeholder="Birthday" />
              </Field>
              <Field>
                <Lbl>💍 Anniversary</Lbl>
                <DateInp value={form.anniversary} onChange={(v) => set("anniversary", v)} placeholder="Anniversary" />
              </Field>
            </div>
          </div>

          <div style={{ background: "#f0fdf4", borderRadius: 14, padding: "16px 18px", border: "1px solid #bbf7d0", marginBottom: 14 }}>
            <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 14 }}>
              <div style={{ width: 28, height: 28, borderRadius: 8, background: C.greenLt, display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
                <i className="fas fa-bed" style={{ color: C.green, fontSize: "0.72rem" }} />
              </div>
              <div style={{ fontSize: "0.78rem", fontWeight: 800, color: C.text }}>Stay Details</div>
              <div style={{ fontSize: "0.71rem", color: C.subtle }}>— powers check-in / out triggers</div>
            </div>

            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: 12 }}>
              <Field>
                <Lbl>Room No.</Lbl>
                <Inp icon={<i className="fas fa-door-open" />} value={form.room_number} onChange={(e) => set("room_number", e.target.value)} placeholder="204" />
              </Field>
              <Field>
                <Lbl>Check-in</Lbl>
                <DateInp value={form.checkin_date} onChange={(v) => set("checkin_date", v)} />
              </Field>
              <Field>
                <Lbl>Check-out</Lbl>
                <DateInp value={form.checkout_date} onChange={(v) => set("checkout_date", v)} />
              </Field>
            </div>
          </div>

          <div style={{ background: "#fefce8", borderRadius: 14, padding: "16px 18px", border: "1px solid #fde68a", marginBottom: 14 }}>
            <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 12 }}>
              <div style={{ width: 28, height: 28, borderRadius: 8, background: "#fef9c3", display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
                <i className="fas fa-tags" style={{ color: C.amber, fontSize: "0.72rem" }} />
              </div>
              <div style={{ fontSize: "0.78rem", fontWeight: 800, color: C.text }}>Tags</div>
              <div style={{ fontSize: "0.71rem", color: C.subtle }}>— segment for targeted campaigns</div>
            </div>

            <div style={{ display: "flex", flexWrap: "wrap", gap: 7, marginBottom: 12 }}>
              {TAGS.map((tag) => {
                const on = (form.tags || []).includes(tag);
                return (
                  <button
                    key={tag}
                    onClick={() => toggleTag(tag)}
                    style={{
                      padding: "5px 12px",
                      borderRadius: 20,
                      cursor: "pointer",
                      border: `1.5px solid ${on ? C.brand : C.border}`,
                      background: on ? C.brand : C.white,
                      color: on ? C.white : C.muted,
                      fontSize: "0.77rem",
                      fontWeight: on ? 700 : 500,
                      display: "inline-flex",
                      alignItems: "center",
                      gap: 5,
                    }}
                  >
                    {tag}
                  </button>
                );
              })}
            </div>

            <div style={{ display: "flex", gap: 8, marginBottom: 10 }}>
              <div style={{ flex: 1, display: "flex", borderRadius: 9, overflow: "hidden", border: `1px solid ${C.border}`, background: C.white }}>
                <input
                  value={tagInput}
                  onChange={(e) => setTagInput(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === "Enter") {
                      e.preventDefault();
                      addCustomTag();
                    }
                  }}
                  placeholder="Add custom tag and press Enter…"
                  style={{ flex: 1, padding: "7px 11px", border: "none", outline: "none", fontSize: "0.8rem", color: C.text, background: "transparent" }}
                />
              </div>
              <button
                onClick={addCustomTag}
                disabled={!tagInput.trim()}
                style={{
                  padding: "7px 14px",
                  borderRadius: 9,
                  border: `1px solid ${tagInput.trim() ? C.brand : C.border}`,
                  background: tagInput.trim() ? C.brandLt : C.bg,
                  color: tagInput.trim() ? C.brand : C.subtle,
                  fontSize: "0.78rem",
                  fontWeight: 700,
                  cursor: tagInput.trim() ? "pointer" : "default",
                }}
              >
                + Add
              </button>
            </div>
          </div>

          <div style={{ background: C.bg, borderRadius: 14, padding: "16px 18px", border: `1px solid ${C.border}` }}>
            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12, marginBottom: 12 }}>
              <Field>
                <Lbl>Source</Lbl>
                <Sel value={form.source} onChange={(v) => set("source", v)} options={SOURCES} />
              </Field>
              <Field>
                <Lbl>Status</Lbl>
                <Sel value={form.status} onChange={(v) => set("status", v)} options={STATUS_OPTIONS} />
              </Field>
            </div>
            <Field>
              <Lbl>Notes</Lbl>
              <textarea
                value={form.notes || ""}
                onChange={(e) => set("notes", e.target.value)}
                placeholder="Special preferences, allergies, VIP instructions, dietary requirements..."
                rows={3}
                style={{ width: "100%", padding: "9px 12px", fontSize: "0.84rem", border: `1px solid ${C.border}`, borderRadius: 9, outline: "none", color: C.text, background: C.white, resize: "vertical", boxSizing: "border-box", fontFamily: "inherit", lineHeight: 1.6 }}
              />
            </Field>
          </div>
        </div>

        <div style={{ padding: "14px 24px", borderTop: `1px solid ${C.border}`, flexShrink: 0, background: C.white, display: "flex", gap: 10 }}>
          <button onClick={onClose} style={{ flex: 1, padding: "10px 0", borderRadius: 9, border: `1px solid ${C.border}`, background: C.white, color: C.text, fontSize: "0.84rem", fontWeight: 600, cursor: "pointer" }}>Cancel</button>
          <button onClick={() => onSave(form)} disabled={saving} style={{ flex: 2, padding: "10px 0", borderRadius: 9, border: "none", background: saving ? "#a5b4fc" : C.brand, color: C.white, fontSize: "0.84rem", fontWeight: 700, cursor: saving ? "not-allowed" : "pointer" }}>
            {saving ? "Saving…" : isEdit ? "Save Changes" : "Add Guest"}
          </button>
        </div>
      </div>
    </div>
  );
};

const CSVImportPanel = ({ show, onHide, onImported }) => {
  const notify = useNotify();
  const fileRef = useRef();
  const [rows, setRows] = useState([]);
  const [errors, setErrors] = useState([]);
  const [stage, setStage] = useState("upload");
  const [saving, setSaving] = useState(false);
  const [progress, setProgress] = useState(0);

  const parseCSV = (text) => {
    const lines = text.trim().split("\n");
    const header = lines[0].split(",").map((h) => h.trim().toLowerCase().replace(/\s+/g, "_"));
    return lines.slice(1).map((line, i) => {
      const vals = line.split(",").map((v) => v.trim().replace(/^"|"$/g, ""));
      const obj = {};
      header.forEach((h, j) => {
        obj[h] = vals[j] || "";
      });
      obj._row = i + 2;
      return obj;
    });
  };

  const validateRows = (list) => {
    const errs = [];
    list.forEach((r) => {
      if (!r.name) errs.push(`Row ${r._row}: Name is required`);
      if (!r.phone) errs.push(`Row ${r._row}: Phone is required`);
    });
    return errs;
  };

  const handleFile = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onload = (ev) => {
      const parsed = parseCSV(ev.target.result);
      setRows(parsed);
      setErrors(validateRows(parsed));
      setStage("preview");
    };
    reader.readAsText(file);
    e.target.value = "";
  };

  const doImport = async () => {
    setSaving(true);
    const customers = rows.map((r) => ({
      name: r.name,
      phone: r.phone,
      email: r.email || "",
      dob: r.dob || r.birthday || "",
      anniversary: r.anniversary || "",
      room_number: r.room_number || "",
      checkin_date: r.checkin_date || "",
      checkout_date: r.checkout_date || "",
      tags: r.tags ? r.tags.split("|").map((t) => t.trim()) : [],
      notes: r.notes || "",
      source: "CSV Import",
      status: "Active",
    }));

    try {
      const BATCH = 50;
      for (let i = 0; i < customers.length; i += BATCH) {
        await axios.post(`${apiUrl}/feedback/guests/bulk`, customers.slice(i, i + BATCH), authHeaders());
        setProgress(Math.min(100, Math.round(((i + BATCH) / customers.length) * 100)));
      }
    } catch {}

    setStage("done");
    setSaving(false);
    onImported(customers);
    notify("success", `${customers.length} customers imported successfully.`);
  };

  const downloadTemplate = () => {
    const header = CSV_HEADERS.join(",");
    const sample = `"Rahul Sharma","+91 98765 43210","rahul@example.com","1990-01-15","2018-03-20","204","2025-01-15","2025-01-17","VIP|Loyalty Member","Prefers sea view"`;
    const blob = new Blob([`${header}\n${sample}`], { type: "text/csv" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = "netshine_customer_import_template.csv";
    a.click();
    URL.revokeObjectURL(url);
  };

  const reset = () => {
    setRows([]);
    setErrors([]);
    setStage("upload");
    setProgress(0);
  };

  return (
    <RightModal
      show={show}
      onHide={() => {
        onHide();
        reset();
      }}
      title={<><FaFileImport className="me-2 text-primary" />Import Customers from CSV</>}
      size="lg"
      footer={stage === "preview" && !saving ? <><Button variant="secondary" onClick={reset}>← Back</Button><Button variant="primary" onClick={doImport}>Import {rows.length} Customers</Button></> : null}
    >
      {stage === "upload" && (
        <div>
          <div className="alert alert-info d-flex gap-3 mb-4">
            <i className="fas fa-info-circle text-info mt-1"></i>
            <div style={{ fontSize: "0.84rem" }}>
              Upload CSV from your <strong>Hotel PMS</strong> or download our template.
            </div>
          </div>
          <div className="d-flex gap-3 mb-4">
            <Button variant="outline-primary" onClick={downloadTemplate}><FaDownload className="me-2" />Download Template</Button>
            <Button variant="primary" onClick={() => fileRef.current?.click()}><FaUpload className="me-2" />Choose CSV File</Button>
            <input ref={fileRef} type="file" accept=".csv,.txt" style={{ display: "none" }} onChange={handleFile} />
          </div>
        </div>
      )}
      {stage === "preview" && (
        <div>
          {errors.length > 0 && <Alert variant="warning" className="py-2 mb-3" style={{ maxHeight: 100, overflowY: "auto", fontSize: "0.78rem" }}>{errors.map((e, i) => <div key={i}>{e}</div>)}</Alert>}
          <div style={{ maxHeight: 400, overflowY: "auto" }}>
            <Table bordered hover size="sm" style={{ fontSize: "0.78rem" }}>
              <thead className="table-light"><tr><th>#</th><th>Name</th><th>Phone</th><th>Email</th><th>DOB</th><th>Tags</th></tr></thead>
              <tbody>
                {rows.slice(0, 100).map((r, i) => (
                  <tr key={i}><td>{r._row}</td><td>{r.name}</td><td>{r.phone}</td><td>{r.email}</td><td>{r.dob}</td><td>{r.tags}</td></tr>
                ))}
              </tbody>
            </Table>
          </div>
        </div>
      )}
      {stage === "done" && <div className="text-center py-4"><h5 className="fw-bold mt-3">Import Complete!</h5><p className="text-muted">{rows.length} customers imported.</p><Button variant="primary" onClick={() => { onHide(); reset(); }}>Close</Button></div>}
    </RightModal>
  );
};

const QRRegistrationModal = ({ show, onHide }) => {
  const companyLogo = localStorage.getItem("companylogo");
  const companyId = localStorage.getItem("companyid") || "demo";
  const hotelName = localStorage.getItem("companyname") || "Our Hotel";
  const baseUrl = process.env.REACT_APP_SURVEY_BASE_URL || "https://secure.netshineoffice.com";
  const regUrl = `${baseUrl}/guest-register?org=${companyId}&offer=30off`;
  const [copied, setCopied] = useState(false);
  const copy = () => {
    navigator.clipboard?.writeText?.(regUrl);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  return (
    <RightModal
      show={show}
      onHide={onHide}
      title={<><FaQrcode className="me-2 text-warning" />Customer Self-Registration QR</>}
      size="md"
      footer={<><Button variant="outline-secondary" onClick={copy}>{copied ? "Copied" : "Copy Link"}</Button><Button variant="primary" onClick={() => window.print()}>Print</Button></>}
    >
      <div className="text-center mb-4">
        <div className="d-inline-block p-4 rounded shadow" style={{ background: "#fff", border: "3px solid #0d6efd", minWidth: 260 }}>
          {companyLogo && <img src={`${cdnUrl}/${companyLogo}`} alt="Logo" style={{ height: 40, objectFit: "contain", marginBottom: 12 }} onError={(e) => { e.target.style.display = "none"; }} />}
          <div className="fw-bold text-dark mb-1">{hotelName}</div>
          <div className="d-flex justify-content-center mb-3"><QRCode id="guest-reg-qr" value={regUrl} size={180} fgColor="#0d6efd" bgColor="#ffffff" level="H" /></div>
          <div className="fw-bold text-danger" style={{ fontSize: "1.1rem" }}>30% OFF</div>
        </div>
      </div>
    </RightModal>
  );
};

const CustomerProfileModal = ({ customer, show, onHide, onEdit }) => {
  if (!customer) return null;
  const initials = customer.name?.split(" ").map((w) => w[0]).join("").slice(0, 2).toUpperCase();
  return (
    <RightModal
      show={show}
      onHide={onHide}
      title={<><FaUser className="text-primary me-2" />Customer Profile</>}
      size="lg"
      footer={<><Button variant="outline-primary" onClick={() => { onHide(); onEdit(customer); }}><FaEdit className="me-2" />Edit Profile</Button><Button variant="secondary" onClick={onHide}>Close</Button></>}
    >
      <Row className="g-4">
        <Col md={5}>
          <div className="text-center mb-4">
            <div className="d-inline-flex align-items-center justify-content-center rounded-circle fw-bold text-white mb-3" style={{ width: 72, height: 72, background: "#0d6efd", fontSize: "1.6rem" }}>{initials}</div>
            <h5 className="fw-bold mb-1">{customer.name}</h5>
          </div>
          {customer.tags?.length > 0 && <div className="mt-3">{customer.tags.map((t) => <TagPill key={t} tag={t} />)}</div>}
        </Col>
        <Col md={7}>
          <h6 className="fw-semibold mb-3"><FaHistory className="text-primary me-2" />Engagement History</h6>
          <div className="p-3 rounded border" style={{ background: "#f0f4ff" }}>
            <small className="fw-semibold text-primary">ACTIVE JOURNEY TRIGGERS</small>
            <div className="mt-2" style={{ fontSize: "0.8rem" }}>
              {customer.dob && <div className="mb-1">🎂 Birthday journey</div>}
              {customer.anniversary && <div className="mb-1">💍 Anniversary journey</div>}
              {customer.checkin_date && <div className="mb-1">🏨 Check-in journey</div>}
            </div>
          </div>
        </Col>
      </Row>
    </RightModal>
  );
};

const GuestCRM = () => {
  const notify = useNotify();
  const [customers, setCustomers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [filterSource, setFilterSource] = useState("");
  const [filterTag, setFilterTag] = useState("");
  const [filterStatus, setFilterStatus] = useState("Active");
  const [selected, setSelected] = useState(null);
  const [showProfile, setShowProfile] = useState(false);
  const [showForm, setShowForm] = useState(false);
  const [showImport, setShowImport] = useState(false);
  const [showQR, setShowQR] = useState(false);
  const [saving, setSaving] = useState(false);
  const [formError, setFormError] = useState("");
  const [showConfirm, setShowConfirm] = useState(false);
  const [confirmData, setConfirmData] = useState(null);
  const [formValue, setFormValue] = useState(emptyCustomer());

  useEffect(() => {
    fetchCustomers();
  }, []);

  const fetchCustomers = async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${apiUrl}/feedback/guests`, authHeaders());
      const raw = Array.isArray(res.data) ? res.data : res.data?.guests || [];
      const normalized = raw.map((g) => ({ ...g, id: g.guestid || g.id, tags: Array.isArray(g.tags) ? g.tags : g.tags ? String(g.tags).split("|") : [] }));
      setCustomers(normalized);
    } catch {
      setCustomers([]);
    } finally {
      setLoading(false);
    }
  };

  const filtered = customers.filter((c) => {
    const matchSearch = !search || c.name?.toLowerCase().includes(search.toLowerCase()) || c.phone?.includes(search) || c.email?.toLowerCase().includes(search.toLowerCase()) || c.room_number?.includes(search);
    const matchSource = !filterSource || c.source === filterSource;
    const matchTag = !filterTag || c.tags?.includes(filterTag);
    const matchStatus = !filterStatus || filterStatus === "All" || c.status === filterStatus;
    return matchSearch && matchSource && matchTag && matchStatus;
  });

  const saveCustomer = async (form) => {
    if (!form.name?.trim() || !form.phone?.trim()) {
      setFormError("Name and phone are required.");
      return;
    }
    setSaving(true);
    setFormError("");
    const id = form.guestid || form.id;
    try {
      if (id) {
        await axios.put(`${apiUrl}/feedback/guests/${id}`, form, authHeaders());
        setCustomers((p) => p.map((c) => ((c.guestid || c.id) === id ? { ...form, id } : c)));
        notify("success", `${form.name} updated.`);
      } else {
        const res = await axios.post(`${apiUrl}/feedback/guests`, form, authHeaders());
        const saved = { ...(res.data || form), id: res.data?.guestid || `c${Date.now()}` };
        setCustomers((p) => [saved, ...p]);
        notify("success", `${form.name} added to CRM.`);
      }
      setShowForm(false);
    } catch {
      const saved = { ...form, id: id || `c${Date.now()}` };
      setCustomers((p) => (id ? p.map((c) => ((c.guestid || c.id) === id ? saved : c)) : [saved, ...p]));
      setShowForm(false);
      notify("info", "Saved (demo mode).");
    } finally {
      setSaving(false);
    }
  };

  const deleteCustomer = async () => {
    const id = confirmData?.guestid || confirmData?.id;
    try {
      await axios.delete(`${apiUrl}/feedback/guests/${id}`, authHeaders());
    } catch {}
    setCustomers((p) => p.filter((c) => (c.guestid || c.id) !== id));
    setShowConfirm(false);
    notify("success", `${confirmData.name} removed.`);
  };

  const handleExportCSV = () => {
    const exportData = filtered.map((c) => ({ ...c, tags: (c.tags || []).join("|") }));
    const headers = ["Name", "Phone", "Email", "Birthday", "Anniversary", "Room", "Check-in", "Check-out", "Source", "Status", "Tags"];
    const rows = exportData.map((r) => [`"${r.name ?? ""}"`, `"${r.phone ?? ""}"`, `"${r.email ?? ""}"`, `"${r.dob ?? ""}"`, `"${r.anniversary ?? ""}"`, `"${r.room_number ?? ""}"`, `"${r.checkin_date ?? ""}"`, `"${r.checkout_date ?? ""}"`, `"${r.source ?? ""}"`, `"${r.status ?? ""}"`, `"${r.tags ?? ""}"`].join(","));
    const blob = new Blob([`${headers.join(",")}\n${rows.join("\n")}`], { type: "text/csv" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `customers_${dayjs().format("YYYYMMDD")}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  const stats = {
    withDOB: customers.filter((c) => c.dob).length,
    withAnniv: customers.filter((c) => c.anniversary).length,
    vip: customers.filter((c) => c.tags?.includes("VIP")).length,
    bySource: SOURCES.reduce((acc, s) => ({ ...acc, [s]: customers.filter((c) => c.source === s).length }), {}),
  };

  const downloadMenu = (
    <Menu>
      <Menu.Item key="csv" onClick={handleExportCSV}>Download CSV</Menu.Item>
      <Menu.Item key="xls" onClick={handleExportCSV}>Download XLS</Menu.Item>
    </Menu>
  );

  if (loading) return <div className="d-flex justify-content-center py-5"><Spinner animation="border" variant="primary" /></div>;

  return (
    <div>
      <div className="page-header mb-4">
        <div className="d-flex justify-content-between align-items-center">
          <div>
            <h3 className="fw-bold text-dark mb-0"><i className="fas fa-users text-primary me-2"></i>Customers</h3>
            <p className="text-muted mb-0 mt-1">{customers.length} customers · {stats.withDOB} with birthday · {stats.withAnniv} with anniversary · {stats.vip} VIP</p>
          </div>
          <div className="d-flex gap-2 align-items-center">
            <Button variant="outline-info" onClick={() => setShowImport(true)}><FaFileImport className="me-1" />Import CSV</Button>
            <Dropdown overlay={downloadMenu} trigger={["click"]} placement="bottomRight"><Button className="btn btn-outline-success d-inline-flex align-items-center">Download <DownOutlined className="ms-2" /></Button></Dropdown>
            <Button variant="outline-warning" onClick={() => setShowQR(true)}><FaQrcode className="me-1" />QR Register</Button>
            <Button variant="primary" className="px-4" onClick={() => { setFormValue(emptyCustomer()); setFormError(""); setShowForm(true); }}><FaPlus className="me-2" />Add Customer</Button>
          </div>
        </div>
      </div>

      <Row className="g-3 mb-4">
        {SOURCES.map((s) => (
          <Col key={s}>
            <Card className="shadow-sm border-0 text-center py-3" style={{ cursor: "pointer", borderLeft: filterSource === s ? "3px solid #0d6efd" : "3px solid transparent" }} onClick={() => setFilterSource(filterSource === s ? "" : s)}>
              <div><i className={`fas ${SOURCE_ICON[s]} text-${SOURCE_COLOR[s]}`} style={{ fontSize: "1.2rem" }}></i><div className="fw-bold text-dark h5 mb-0 mt-1">{stats.bySource[s] || 0}</div><small className="text-muted" style={{ fontSize: "0.7rem" }}>{s}</small></div>
            </Card>
          </Col>
        ))}
      </Row>

      <Card className="shadow-sm border-0 mb-3">
        <Card.Body className="py-3 px-4">
          <Row className="g-2 align-items-center">
            <Col md={4}><InputGroup><InputGroup.Text className="bg-white"><FaSearch className="text-muted" /></InputGroup.Text><Form.Control value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Search name, phone, email, room..." /></InputGroup></Col>
            <Col md={3}><RSelect options={["All Sources", ...SOURCES]} value={filterSource || "All Sources"} onChange={(v) => setFilterSource(v === "All Sources" ? "" : v)} isClearable={false} /></Col>
            <Col md={2}><RSelect options={["All Tags", ...TAGS]} value={filterTag || "All Tags"} onChange={(v) => setFilterTag(v === "All Tags" ? "" : v)} isClearable={false} /></Col>
            <Col md={2}><RSelect options={["All", ...STATUS_OPTIONS]} value={filterStatus} onChange={(v) => setFilterStatus(v)} isClearable={false} /></Col>
            <Col md={1} className="text-end"><small className="text-muted">{filtered.length} shown</small></Col>
          </Row>
        </Card.Body>
      </Card>

      <Card className="shadow-sm border-0">
        <Card.Body className="p-0">
          <div className="table-responsive">
            <Table hover className="mb-0">
              <thead className="table-light"><tr>{["Customer", "Phone", "Source", "Room / Stay", "Birthday", "Anniversary", "Tags", "Status", ""].map((h, i) => <th key={i} className={`py-3 border-0 fw-semibold text-muted ${i === 0 ? "ps-4" : ""} ${i === 8 ? "text-center pe-4" : ""}`} style={{ fontSize: "0.73rem", textTransform: "uppercase", whiteSpace: "nowrap" }}>{h}</th>)}</tr></thead>
              <tbody>
                {filtered.map((c) => (
                  <tr key={c.id} className="align-middle" style={{ cursor: "pointer" }} onClick={() => { setSelected(c); setShowProfile(true); }}>
                    <td className="ps-4"><div className="fw-semibold text-dark">{c.name}</div>{c.email && <div className="text-muted" style={{ fontSize: "0.75rem" }}>{c.email}</div>}</td>
                    <td className="text-muted">{c.phone}</td>
                    <td><Badge bg={SOURCE_COLOR[c.source] || "secondary"}><i className={`fas ${SOURCE_ICON[c.source] || "fa-user"} me-1`}></i>{c.source}</Badge></td>
                    <td>{c.room_number ? <div><div className="fw-semibold" style={{ fontSize: "0.82rem" }}><FaBed className="me-1 text-info" />Rm {c.room_number}</div>{c.checkin_date && <div className="text-muted" style={{ fontSize: "0.72rem" }}>{dayjs(c.checkin_date).format("DD MMM")} → {dayjs(c.checkout_date).format("DD MMM")}</div>}</div> : <span className="text-muted">—</span>}</td>
                    <td>{c.dob ? `🎂 ${dayjs(c.dob).format("DD MMM")}` : <span className="text-muted">—</span>}</td>
                    <td>{c.anniversary ? `💍 ${dayjs(c.anniversary).format("DD MMM")}` : <span className="text-muted">—</span>}</td>
                    <td><div className="d-flex flex-wrap gap-1">{(c.tags || []).slice(0, 2).map((t) => <TagPill key={t} tag={t} />)}</div></td>
                    <td><Badge bg={c.status === "Active" ? "success" : c.status === "Blocked" ? "danger" : "secondary"} pill>{c.status}</Badge></td>
                    <td className="text-center pe-4" onClick={(e) => e.stopPropagation()}><Button variant="light" size="sm" className="p-2 me-1" onClick={() => { setFormValue(c); setFormError(""); setShowForm(true); }}><FaEdit className="text-primary" /></Button><Button variant="light" size="sm" className="p-2" onClick={() => { setConfirmData(c); setShowConfirm(true); }}><FaTrash className="text-danger" /></Button></td>
                  </tr>
                ))}
                {filtered.length === 0 && <tr><td colSpan={9} className="text-center py-5 text-muted"><FaUser size={28} className="mb-2 d-block mx-auto" color="#dee2e6" />No customers found.</td></tr>}
              </tbody>
            </Table>
          </div>
        </Card.Body>
      </Card>

      <GuestFormPanel show={showForm} editingCustomer={formValue} onSave={saveCustomer} onClose={() => setShowForm(false)} saving={saving} error={formError} />
      <CustomerProfileModal customer={selected} show={showProfile} onHide={() => setShowProfile(false)} onEdit={(c) => { setFormValue(c); setFormError(""); setShowForm(true); }} />
      <GuestImport show={showImport} onClose={() => setShowImport(false)} onSuccess={() => { fetchCustomers(); setShowImport(false); }} />
      <QRRegistrationModal show={showQR} onHide={() => setShowQR(false)} />
      <CSVImportPanel show={false} onHide={() => {}} onImported={() => {}} />

      <RightModal show={showConfirm} onHide={() => setShowConfirm(false)} title="Confirm Delete" size="sm" footer={<><Button variant="secondary" onClick={() => setShowConfirm(false)}>Cancel</Button><Button variant="danger" onClick={deleteCustomer}>Delete Customer</Button></>}>
        <p>Delete <strong>{confirmData?.name}</strong>?</p>
        <p className="text-muted small">All their engagement history will also be removed. This cannot be undone.</p>
      </RightModal>

      <style>{`.card { border-radius: 12px !important; } .card-header { border-radius: 12px 12px 0 0 !important; } .page-header { margin-bottom: 1.5rem; }`}</style>
    </div>
  );
};

export default GuestCRM;
